<template>
  <div
    class="work-schedule-panel"
    :class="{ 'work-schedule-panel--mobile': isMobile }"
  >
    <WorkScheduleSkeleton v-if="isLoading && !schedule" />

    <template v-else-if="schedule">
      <div class="work-schedule-panel__days">
        <div
          v-for="day in scheduleDays"
          :key="day"
          class="work-schedule-panel__day"
          :class="{
            'text-subtitle-1 work-schedule-panel__day--current': day === schedule.now_weekday_translit,
            'text-body-1': day !== schedule.now_weekday_translit,
          }"
        >
          <div class="work-schedule-panel__cell work-schedule-panel__cell--day">
            {{ tStatic(schedule.full_days[day]) }}
          </div>
          <div class="work-schedule-panel__cell work-schedule-panel__cell--time">
            {{ tStatic(schedule.schedule_days[day]) }}
          </div>
        </div>
      </div>

      <template v-if="schedule.lpu_exceptions.length">
        <v-divider
          v-if="!isMobile"
          class="work-schedule-panel__divider"
        />

        <div :class="{ 'mt-6': isMobile }">
          <div
            class="text--text"
            :class="isMobile ? 'text-h6 mb-4' : 'text-subtitle-1'"
          >
            {{ holidaysTitle }}
          </div>
          <div class="work-schedule-panel__days">
            <div
              v-for="(exception, index) in schedule.lpu_exceptions"
              :key="`holiday-${index}`"
              class="work-schedule-panel__day text-body-1"
            >
              <div class="work-schedule-panel__cell work-schedule-panel__cell--day">
                {{ tStatic(exception.date) }}
              </div>
              <div class="work-schedule-panel__cell work-schedule-panel__cell--time">
                {{ tStatic(exception.time) }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { LpuScheduleResponse } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/components/WorkSchedule/api/apiLpuSchedule.types'
import WorkScheduleSkeleton from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/components/WorkSchedule/components/WorkScheduleSkeleton.vue'

const props = defineProps<{
  schedule: LpuScheduleResponse | null
  isLoading?: boolean
}>()

const isMobile = useIsMobile()

const scheduleDays = computed(() => (
  props.schedule
    ? Object.keys(props.schedule.schedule_days)
    : []
))

const holidaysTitle = computed(() => (
  isMobile.value
    ? tStatic('График работы в праздничные дни')
    : tStatic('График работы в праздники')
))
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.work-schedule-panel {
  // Desktop: одна колонка, день 110px, gap 30 / 16
  display: flex;
  flex-direction: column;
  width: 100%;

  &__days {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
  }

  &__day {
    display: flex;
    align-items: center;
    column-gap: 30px;
    color: $ui-kit-text-info;

    &--current {
      color: $ui-kit-text;
    }
  }

  &__cell {
    &--day {
      flex: 0 0 110px;
      width: 110px;
    }

    &--time {
      flex-shrink: 0;
      white-space: nowrap;
    }
  }

  &__divider {
    margin: 16px 0;
  }

  // Mobile — как legacy LpuSchedule
  &--mobile {
    .work-schedule-panel__days {
      row-gap: 12px;
    }

    .work-schedule-panel__day {
      column-gap: 16px;
      color: $ui-kit-text-secondary;

      &--current {
        color: $ui-kit-text;
      }
    }

    .work-schedule-panel__cell {
      flex: 0 1 50%;
      width: auto;

      &--day,
      &--time {
        flex: 0 1 50%;
        width: auto;
        white-space: normal;
      }
    }
  }
}
</style>
